import React, { useState, useRef, useEffect } from "react";
import { Link } from "gatsby";
import Transition from "../utils/Transition.js";
import NavBarDropdown from "../utils/NavBarDropdown.js";
import NavBarProducts from "./NavBarProducts.js";
import NavBarCompany from "./NavBarCompany.js";

function NavBar({ theme = "light" }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [top, setTop] = useState(true);

  const trigger = useRef(null);
  const mobileNav = useRef(null);

  let logo =
    require("../images/rhoimpact/rhoimpact_logo-horizontal_color.png").default;
  let navTextColor = "text-rhoGray-500 hover:text-rhoGray-900";
  let navBgColor = "bg-rhoOffWhite-100";
  if (theme === "dark") {
    logo =
      require("../images/rhoimpact/rhoimpact_logo-horizontal_white.png").default;
    navTextColor = "text-rhoOffWhite-100 hover:text-rhoGray-200";
    navBgColor = "bg-rhoBlueDark-700";
  }

  // close the mobile menu on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!mobileNav.current || !trigger.current) return;
      if (
        !mobileNavOpen ||
        mobileNav.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setMobileNavOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close the mobile menu if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!mobileNavOpen || keyCode !== 27) return;
      setMobileNavOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  // detect whether user has scrolled the page down by 10px
  useEffect(() => {
    const scrollHandler = () => {
      window.pageYOffset > 10 ? setTop(false) : setTop(true);
    };
    window.addEventListener("scroll", scrollHandler);
    return () => window.removeEventListener("scroll", scrollHandler);
  }, [top]);

  return (
    <header
      className={`fixed w-full z-30 md:bg-opacity-90 transition duration-300 ease-in-out
                 backdrop-blur shadow-lg h-20 ${navBgColor} ${!top && ""}`}
    >
      <div className="max-w-6xl px-5 mx-auto sm:px-6 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Site branding */}
          <div className="flex-shrink-0 mr-4">
            {/* Logo */}
            <Link to="/" className="block" aria-label="Rho Impact">
              <img
                className="h-auto max-w-full mx-auto md:max-w-none"
                src={logo}
                width="150"
                alt="Rho Impact Logo"
              />
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex lg:flex-grow h-full">
            {/* Desktop menu links */}
            <ul className="flex flex-wrap items-center justify-end flex-grow h-full">
              <NavBarDropdown theme={theme} title="Products">
                <NavBarProducts mobile={false} />
              </NavBarDropdown>
              <NavBarDropdown theme={theme} title="Company">
                <NavBarCompany mobile={false} />
              </NavBarDropdown>
            </ul>

            {/* Desktop sign in links */}
            <ul className="flex flex-wrap items-center justify-end flex-grow">
              <li>
                <a
                  className={
                    "flex items-center px-5 py-3 font-medium transition duration-150 ease-in-out " +
                    navTextColor
                  }
                  href="https://app.koi.eco/login"
                  target="_blank"
                  rel="noreferrer"
                >
                  Login
                </a>
              </li>
              <li>
                <a
                  href="https://app.koi.eco/signup"
                  target="_blank"
                  rel="noreferrer"
                  className="group ml-3 text-rhoBlueDark-700 btn-sm bg-rhoLime-700 hover:bg-rhoLime-700"
                >
                  <span>Start for Free</span>&nbsp;&nbsp;
                  <span className="inline-block transform transition-transform duration-300 group-hover:translate-x-2">
                    <i className="fa-solid fa-arrow-right"></i>
                  </span>
                </a>
              </li>
            </ul>
          </nav>

          {/* Mobile menu */}
          <div className="flex lg:hidden">
            {/* Hamburger button */}
            <button
              ref={trigger}
              className={`hamburger ${mobileNavOpen && "active"}`}
              aria-controls="mobile-nav"
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
            >
              <span className="sr-only">Menu</span>
              <svg
                className={"w-6 h-6 fill-current " + navTextColor}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect y="4" width="24" height="2" />
                <rect y="11" width="24" height="2" />
                <rect y="18" width="24" height="2" />
              </svg>
            </button>

            {/*Mobile navigation */}
            <div ref={mobileNav}>
              <Transition
                show={mobileNavOpen}
                tag="nav"
                id="mobile-nav"
                className="absolute left-0 z-20 w-full h-screen pb-16 overflow-scroll bg-white top-full"
                enter="transition ease-out duration-200 transform"
                enterStart="opacity-0 -translate-y-2"
                enterEnd="opacity-100 translate-y-0"
                leave="transition ease-out duration-200"
                leaveStart="opacity-100"
                leaveEnd="opacity-0"
              >
                <ul className="px-5 py-2">
                  <li className="py-2 my-2 border-b border-gray-200">
                    <span className="flex py-2 text-gray-600 hover:text-gray-900">
                      Products
                    </span>
                    <ul className="pl-4">
                      <NavBarProducts mobile={true} />
                    </ul>
                  </li>
                  <li className="py-2 my-2 border-b border-gray-200">
                    <Link to="/about/">
                      <span className="flex py-2 text-gray-600 hover:text-gray-900">
                        Company
                      </span>
                    </Link>
                    <ul className="pl-4">
                      <NavBarCompany mobile={true} />
                    </ul>
                  </li>

                  <li>
                    <Link
                      to="/contact/?topic=Demo"
                      className="w-full mt-2 mb-12 text-gray-200 bg-gray-900 btn-sm hover:bg-gray-800"
                    >
                      <span>Book a Demo</span>
                      <svg
                        className="flex-shrink-0 w-3 h-3 ml-2 -mr-1 text-gray-400 fill-current"
                        viewBox="0 0 12 12"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.707 5.293L7 .586 5.586 2l3 3H0v2h8.586l-3 3L7 11.414l4.707-4.707a1 1 0 000-1.414z"
                          fill="#999"
                          fillRule="nonzero"
                        />
                      </svg>
                    </Link>
                  </li>
                </ul>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default NavBar;
